import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { CouponsIcon } from "@/components/icon-stack/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { GameStatusEnum } from "@/pages/search/types";

import type { MapLocationType } from "../../types";
import useGamesQuery from "./hooks/use-games-query";
import { NextIcon, PrevIcon } from "./icons";
import LocationCard, { type LocationCardGameType } from "./location-card";

const NAV_BUTTTON_CLASS_NAME =
  "absolute top-1/2 z-1 hidden size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white md:flex";

interface MarkerLocationsProps {
  location: MapLocationType;
}

export default function MarkerLocations({
  location: selectedLocation,
}: MarkerLocationsProps) {
  const [currentGame, setCurrentGame] = useState<LocationCardGameType | null>(
    null
  );
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { data, loading } = useGamesQuery(selectedLocation.id);

  const location = useMemo(
    () => (!loading && data?.location ? data.location : null),
    [data, loading]
  );
  const filteredGameIds = useMemo(
    () => selectedLocation.games.map((game) => game.id),
    [selectedLocation.games]
  );
  const games = useMemo(() => {
    const filteredGames = (location?.games || []).filter((game) =>
      filteredGameIds.includes(game.id)
    );

    const statusWeights = {
      [GameStatusEnum.OP]: 4,
      [GameStatusEnum.CS]: 3,
      [GameStatusEnum.PC]: 2,
      [GameStatusEnum.TC]: 1,
    };

    const assignedGames: string[] = [];
    const addAssignedGames = (games: LocationCardGameType[]) => {
      for (const game of games) {
        assignedGames.push(game.id);
      }
      return games;
    };

    const sortedSavedGames = addAssignedGames(
      filteredGames
        .filter(
          (game) =>
            (game.isSaved && game.status === GameStatusEnum.CS) ||
            game.status === GameStatusEnum.OP
        )
        .sort((a, b) => (b.communityScore || 0) - (a.communityScore || 0))
    );

    const sortedCompletedGames = addAssignedGames(
      filteredGames
        .filter((game) => game.isCompleted && !assignedGames.includes(game.id))
        .sort(
          (a, b) =>
            (b.communityScore || 0) - (a.communityScore || 0) ||
            statusWeights[b.status] - statusWeights[a.status]
        )
    );

    const sortedClosedGames = addAssignedGames(
      filteredGames
        .filter(
          (game) =>
            !assignedGames.includes(game.id) &&
            (game.status === GameStatusEnum.PC ||
              game.status === GameStatusEnum.TC)
        )
        .sort(
          (a, b) =>
            statusWeights[b.status] - statusWeights[a.status] ||
            (b.communityScore || 0) - (a.communityScore || 0)
        )
    );

    const remainingGames = filteredGames.filter(
      (game) => !assignedGames.includes(game.id)
    );

    return [
      ...sortedSavedGames,
      ...remainingGames,
      ...sortedCompletedGames,
      ...sortedClosedGames,
    ];
  }, [location, filteredGameIds]);

  useEffect(() => {
    setCurrentGame(games.length ? games[0] : null);
  }, [games]);

  const currentIndex = useMemo(
    () => (currentGame ? games.findIndex((g) => g.id === currentGame.id) : -1),
    [games, currentGame]
  );

  const handleNavButton =
    (mod = 1) =>
    () => {
      if (currentIndex === -1) {
        return;
      }

      let newIndex = currentIndex + mod;
      const lastIndex = games.length - 1;

      if (newIndex < 0) {
        newIndex = lastIndex;
      }
      if (newIndex > lastIndex) {
        newIndex = 0;
      }

      if (cardRefs.current[newIndex]) {
        cardRefs.current[newIndex]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }

      setCurrentGame(games[newIndex]);
    };

  useEffect(() => {
    if (!scrollContainerRef.current || games.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const index = Number.parseInt(
              entry.target.getAttribute("data-index") || "0",
              10
            );
            setCurrentGame(games[index]);
          }
        }
      },
      {
        root: scrollContainerRef.current,
        threshold: 1,
      }
    );

    for (const ref of cardRefs.current) {
      if (ref) {
        observer.observe(ref);
      }
    }

    return () => observer.disconnect();
  }, [games]);

  const hasMultipleGames = useMemo(() => games.length > 1, [games]);
  const hasCoupons = useMemo(
    () => games.some((game) => !!game?.coupons?.length),
    [games]
  );

  const displayName =
    selectedLocation.displayName || selectedLocation.company.name;
  const locationHref = location
    ? `/location/${location.company.slug}/${location.slug}`
    : undefined;

  return (
    <div
      className={cn(
        "absolute inset-x-2.5 bottom-8 rounded-[10px] bg-[url(/images/main-bg.jpg)] bg-center bg-cover bg-no-repeat pb-[11px] shadow-0-0-20 shadow-black/50 md:inset-x-12.5 md:bottom-24",
        hasMultipleGames ? "md:pb-[15px]" : "md:pb-7.5"
      )}
    >
      <div className="flex items-center justify-between p-[15px] pb-[11px] md:px-[30px] md:pt-[25px] md:pb-[18px]">
        <h1 className="font-semibold text-15 text-white leading-[1.5em] md:text-17">
          {locationHref ? (
            <Link to={locationHref}>
              {displayName}{" "}
              {hasCoupons && <CouponsIcon className="ml-2 inline" />}
            </Link>
          ) : (
            <span>
              {displayName}{" "}
              {hasCoupons && <CouponsIcon className="ml-2 inline" />}
            </span>
          )}
        </h1>
        {location?.locality.locality && (
          <h6 className="text-right font-normal text-12 text-pale-lavender leading-[1.5em] md:text-14">
            {location.locality.locality}
          </h6>
        )}
      </div>
      {loading ? (
        <div className="flex items-center justify-center px-[15px] py-6 md:px-7.5">
          <div className="size-6 animate-spin rounded-full border-2 border-white/20 border-t-white" />
        </div>
      ) : (
        <>
          <div className="relative">
            {hasMultipleGames && (
              <Button
                aria-label="Previous"
                className={cn(NAV_BUTTTON_CLASS_NAME, "-left-5")}
                onClick={handleNavButton(-1)}
                type="button"
              >
                <PrevIcon />
              </Button>
            )}
            <div
              className="scrollbar-hidden flex snap-x snap-mandatory items-stretch gap-2.5 overflow-x-auto px-[15px] md:px-7.5"
              ref={scrollContainerRef}
            >
              {games.map((game, index) => (
                <LocationCard
                  className="snap-center"
                  data-index={index}
                  game={game}
                  hasMultipleGames={hasMultipleGames}
                  key={game.id}
                  ref={(el) => {
                    cardRefs.current[index] = el || null;
                  }}
                />
              ))}
            </div>
            {hasMultipleGames && (
              <Button
                aria-label="Next"
                className={cn(NAV_BUTTTON_CLASS_NAME, "-right-5")}
                onClick={handleNavButton()}
                type="button"
              >
                <NextIcon />
              </Button>
            )}
          </div>
          {hasMultipleGames && (
            <div className="flex justify-center gap-2 pt-[11px] md:pt-11.5">
              {games.map((game) => {
                const isCurrent = currentGame && currentGame.id === game.id;

                return (
                  <Button
                    aria-label={game.name}
                    className={cn(
                      "size-2 rounded-full border-white bg-black-pearl",
                      isCurrent ? "bg-razzmatazz" : "border"
                    )}
                    key={game.id}
                    onClick={() => setCurrentGame(game)}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
